import { useMemo } from 'react';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import {
  SignalIcon,
  Battery50Icon,
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useRealtimeStore, Location } from '../../store/realtimeStore';
import { useUnitStore } from '../../store/unitStore';
import { useAuthStore } from '../../store/authStore';
import PhaseChip from '../PhaseChip';
import { getPlatoon, ACTIVE_SOS_STATUSES } from './mapUtils';

// Total distance of a trail in meters.
function calculateTrailDistance(points: Array<{ latitude: number; longitude: number }>): number {
  if (points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += getDistanceInMeters(
      points[i - 1].latitude,
      points[i - 1].longitude,
      points[i].latitude,
      points[i].longitude
    );
  }
  return total;
}

// Haversine distance in meters.
function getDistanceInMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Detail panel for the selected platoon. Full-width scrollable card on mobile,
// floating w-80 column next to the side panels on desktop.
export default function PlatoonDetailPanel({
  location,
  onClose,
  onTogglePath,
  isPathShown,
  pathLoading,
}: {
  location: Location;
  onClose: () => void;
  onTogglePath?: () => void;
  isPathShown?: boolean;
  pathLoading?: boolean;
}) {
  const { locationHistory, sosEvents } = useRealtimeStore();
  const { getEffectiveUnitIds, units } = useUnitStore();
  const { user } = useAuthStore();

  const platoon = getPlatoon(location);
  const platoonId = location.platoonId || platoon?.id;
  const history = locationHistory.get(location.deviceId);

  const unitId = platoon?.unitId || platoon?.unit?.id;
  const unit = unitId ? units.find((u) => u.id === unitId) : null;

  const effectiveUnitIds = getEffectiveUnitIds(user?.unitId || null);
  const filteredSOSEvents = useMemo(() => {
    if (!effectiveUnitIds) return sosEvents;
    return sosEvents.filter((sos) => effectiveUnitIds.includes(sos.unitId || ''));
  }, [sosEvents, effectiveUnitIds]);

  const isOnline = new Date(location.serverTime).getTime() > Date.now() - 5 * 60 * 1000;
  const hasSOS =
    !!platoon?.sos ||
    filteredSOSEvents.some(
      (sos) =>
        !!platoonId &&
        sos.platoonId === platoonId &&
        !!sos.status &&
        ACTIVE_SOS_STATUSES.includes(sos.status)
    );

  const phase = platoon?.currentPhase;
  const trailDistance = history ? calculateTrailDistance(history.points) : 0;

  return (
    <div className="absolute left-2 right-2 top-2 lg:left-auto lg:right-96 lg:top-4 lg:w-80 z-[1002] max-h-[calc(100%-5rem)] lg:max-h-[calc(100%-2rem)] overflow-y-auto overscroll-contain bg-gray-900/95 border border-gray-700 rounded-xl shadow-2xl">
      {/* Header */}
      <div
        className={clsx(
          'px-4 py-3 flex items-center justify-between',
          hasSOS ? 'bg-red-600' : isOnline ? 'bg-green-600' : 'bg-gray-700'
        )}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <UserGroupIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">{platoon?.callsign || 'Unknown'}</h3>
            <p className="text-white/80 text-xs">
              {hasSOS ? '🚨 SOS ACTIVE' : isOnline ? '● Online' : '○ Offline'}
            </p>
          </div>
        </div>
        <button onClick={onClose} aria-label="Close platoon details" className="p-2.5 -m-1 hover:bg-white/20 rounded-lg transition-colors touch-manipulation">
          <XMarkIcon className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Details */}
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          {platoon?.platoonNumber != null && (
            <DetailRow label="Platoon" value={`Platoon ${platoon.platoonNumber}`} />
          )}
          <DetailRow label="Unit" value={unit ? `${unit.code} - ${unit.name}` : platoon?.unit?.code || 'N/A'} />
          {platoon?.commanderName && <DetailRow label="Commander" value={platoon.commanderName} />}
          {platoon?.strength != null && <DetailRow label="Strength" value={String(platoon.strength)} />}
          {phase && (
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Phase</span>
              <PhaseChip phase={phase} />
            </div>
          )}
        </div>

        {/* Movement */}
        <div className="border-t border-gray-700 pt-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-2">Movement</h4>
          <div className="grid grid-cols-2 gap-3">
            <StatBox
              icon={<MapPinIcon className="w-5 h-5" />}
              label="Distance"
              value={trailDistance > 1000 ? `${(trailDistance / 1000).toFixed(1)} km` : `${Math.round(trailDistance)} m`}
              color="green"
            />
            <StatBox
              icon={<ClockIcon className="w-5 h-5" />}
              label="Waypoints"
              value={String(history?.points.length || 0)}
              color="blue"
            />
          </div>
        </div>

        {/* Device Status */}
        <div className="border-t border-gray-700 pt-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-2">Device Status</h4>
          <div className="grid grid-cols-3 gap-2">
            <MiniStat
              icon={<Battery50Icon className="w-4 h-4" />}
              value={`${location.battery ?? '--'}%`}
              color={location.battery != null && location.battery > 20 ? 'green' : 'red'}
            />
            <MiniStat icon={<SignalIcon className="w-4 h-4" />} value={`${location.signal ?? '--'}`} color="blue" />
            <MiniStat
              icon={<MapPinIcon className="w-4 h-4" />}
              value={`±${location.accuracy?.toFixed(0) ?? '--'}m`}
              color="purple"
            />
          </div>
        </div>

        {/* Route / status */}
        <div className="border-t border-gray-700 pt-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-2">Status</h4>
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Current Speed</span>
              <span className="text-white font-medium">
                {location.speed ? `${(location.speed * 3.6).toFixed(1)} km/h` : '0 km/h'}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-gray-400">Last Update</span>
              <span className="text-white font-medium">
                {formatDistanceToNow(new Date(location.serverTime), { addSuffix: true })}
              </span>
            </div>
          </div>
        </div>

        {/* Coordinates */}
        <div className="border-t border-gray-700 pt-4">
          <h4 className="text-sm font-semibold text-gray-400 mb-2">Current Position</h4>
          <div className="bg-gray-800 rounded-lg p-3 font-mono text-sm">
            <div className="text-green-400">{location.latitude.toFixed(6)}° N</div>
            <div className="text-blue-400">{location.longitude.toFixed(6)}° E</div>
          </div>
        </div>

        {/* Travel Path Button */}
        {onTogglePath && platoonId && (
          <div className="border-t border-gray-700 pt-4">
            <button
              onClick={onTogglePath}
              disabled={pathLoading}
              className={clsx(
                'w-full py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 touch-manipulation',
                isPathShown
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50 hover:bg-orange-500/30'
                  : 'bg-blue-500/20 text-blue-400 border border-blue-500/50 hover:bg-blue-500/30',
                pathLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              {pathLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading Path...
                </>
              ) : isPathShown ? (
                <>
                  <XMarkIcon className="w-5 h-5" />
                  Hide Travel Path
                </>
              ) : (
                <>
                  <MapPinIcon className="w-5 h-5" />
                  Show Travel Path (24h)
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400 text-sm">{label}</span>
      <span className="text-white font-medium text-sm">{value}</span>
    </div>
  );
}

function StatBox({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: 'blue' | 'green' | 'red' | 'purple';
}) {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    red: 'bg-red-500/20 text-red-400',
    purple: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <div className={clsx('rounded-lg p-3', colors[color])}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs opacity-80">{label}</span>
      </div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function MiniStat({
  icon,
  value,
  color,
}: {
  icon: React.ReactNode;
  value: string;
  color: 'blue' | 'green' | 'red' | 'purple';
}) {
  const colors = {
    blue: 'text-blue-400',
    green: 'text-green-400',
    red: 'text-red-400',
    purple: 'text-purple-400',
  };

  return (
    <div className={clsx('flex items-center gap-1 text-sm', colors[color])}>
      {icon}
      <span className="font-medium">{value}</span>
    </div>
  );
}
